import React, { useEffect, useRef } from "react";

type MathJaxConfig = {
  tex: {
    inlineMath: string[][];
    displayMath: string[][];
  };
};

type MathJaxInstance = {
  typesetPromise: (elements?: HTMLElement[]) => Promise<void>;
  startup?: { promise: Promise<void> };
};

declare global {
  interface Window {
    MathJax?: MathJaxConfig | MathJaxInstance;
  }
}

function isMathJaxInstance(
  mathJax: MathJaxConfig | MathJaxInstance
): mathJax is MathJaxInstance {
  return "typesetPromise" in mathJax;
}

let mathJaxLoadPromise: Promise<void> | null = null;

function loadMathJax(): Promise<void> {
  const existing = window.MathJax;
  if (existing && isMathJaxInstance(existing) && existing.startup?.promise) {
    return existing.startup.promise;
  }

  if (!mathJaxLoadPromise) {
    const config: MathJaxConfig = {
      tex: {
        inlineMath: [
          ["\\(", "\\)"],
          ["$", "$"],
        ],
        displayMath: [["\\[", "\\]"]],
      },
    };
    window.MathJax = config;

    mathJaxLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src =
        "https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js";
      script.async = true;
      script.onload = () => {
        const mathJax = window.MathJax;
        if (mathJax && isMathJaxInstance(mathJax) && mathJax.startup?.promise) {
          mathJax.startup.promise.then(resolve).catch(reject);
        } else {
          reject(new Error("MathJax failed to initialize"));
        }
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  return mathJaxLoadPromise;
}

type MathTextProps = {
  text?: string | null;
  className?: string;
  isHtml?: boolean;
};

export const MathText: React.FC<MathTextProps> = ({
  text,
  className,
  isHtml = false,
}) => {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || text == null) return;

    if (isHtml) {
      el.innerHTML = text;
    } else {
      el.textContent = text;
    }

    loadMathJax()
      .then(() => {
        const mathJax = window.MathJax;
        if (mathJax && isMathJaxInstance(mathJax)) {
          return mathJax.typesetPromise([el]);
        }
      })
      .catch(() => {});
  }, [text, isHtml]);

  if (text == null || text === "") return null;

  return <span ref={ref} className={className} />;
};
